import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { FieldOverride } from '@app/interfaces/dynamicFields';
import { ISessionsFilterOptions } from '@app/interfaces/session';
import { tableDataInit } from '@app/pages/FishingOperations/Sessions/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

export interface SessionsFiltersState {
  overrides: FieldOverride[];
  filterOptions: ISessionsFilterOptions;
  shouldTriggerFetch: boolean;
  userSearch: string;
  selectedWaterbodyIds: FilterIdsType;
  selectedWaterbodyIdsTemp: FilterIdsType;
  selectedWaterbodySubtypeIds: FilterIdsType;
  selectedWaterbodySubtypeIdsTemp: FilterIdsType;
  startFrom: Dayjs | null | undefined;
  startTo: Dayjs | null | undefined;
  selectedMembershipTypeIds: FilterIdsType;
  selectedMembershipTypeIdsTemp: FilterIdsType;
  selectedLicenseRangeTypes: string[];
  selectedLicenseRangeTypesTemp: string[];
  selectedLicenseTitles: string[];
  selectedLicenseTitlesTemp: string[];
}

const initialState: SessionsFiltersState = {
  overrides: [],
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: true,
  userSearch: '',
  selectedWaterbodyIds: [],
  selectedWaterbodyIdsTemp: [],
  selectedWaterbodySubtypeIds: [],
  selectedWaterbodySubtypeIdsTemp: [],
  startFrom: undefined,
  startTo: undefined,
  selectedMembershipTypeIds: [],
  selectedMembershipTypeIdsTemp: [],
  selectedLicenseRangeTypes: [],
  selectedLicenseRangeTypesTemp: [],
  selectedLicenseTitles: [],
  selectedLicenseTitlesTemp: [],
};

const sessionsFiltersSlice = createSlice({
  name: 'sessionsFilters',
  initialState,
  reducers: {
    setOverrides(state, action: PayloadAction<FieldOverride[]>) {
      state.overrides = action.payload;
    },
    setFilterOptions(state, action: PayloadAction<ISessionsFilterOptions>) {
      state.filterOptions = action.payload;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setUserSearch(state, action: PayloadAction<string>) {
      state.userSearch = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedWaterbodyIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIdsTemp = action.payload;
    },
    setSelectedWaterbodySubtypeIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodySubtypeIds = action.payload;
    },
    setSelectedWaterbodySubtypeIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodySubtypeIdsTemp = action.payload;
    },
    setStartFrom(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.startFrom = action.payload;
      state.shouldTriggerFetch = true;
    },
    setStartTo(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.startTo = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedMembershipTypeIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipTypeIds = action.payload;
    },
    setSelectedMembershipTypeIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipTypeIdsTemp = action.payload;
    },
    setSelectedLicenseRangeTypes(state, action: PayloadAction<string[]>) {
      state.selectedLicenseRangeTypes = action.payload;
    },
    setSelectedLicenseRangeTypesTemp(state, action: PayloadAction<string[]>) {
      state.selectedLicenseRangeTypesTemp = action.payload;
    },
    setSelectedLicenseTitles(state, action: PayloadAction<string[]>) {
      state.selectedLicenseTitles = action.payload;
    },
    setSelectedLicenseTitlesTemp(state, action: PayloadAction<string[]>) {
      state.selectedLicenseTitlesTemp = action.payload;
    },
    resetFilters(state) {
      state.userSearch = initialState.userSearch;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedWaterbodyIdsTemp = initialState.selectedWaterbodyIdsTemp;
      state.selectedWaterbodySubtypeIds = initialState.selectedWaterbodySubtypeIds;
      state.selectedWaterbodySubtypeIdsTemp = initialState.selectedWaterbodySubtypeIdsTemp;
      state.startFrom = initialState.startFrom;
      state.startTo = initialState.startTo;
      state.selectedMembershipTypeIds = initialState.selectedMembershipTypeIds;
      state.selectedMembershipTypeIdsTemp = initialState.selectedMembershipTypeIdsTemp;
      state.selectedLicenseRangeTypes = initialState.selectedLicenseRangeTypes;
      state.selectedLicenseRangeTypesTemp = initialState.selectedLicenseRangeTypesTemp;
      state.selectedLicenseTitles = initialState.selectedLicenseTitles;
      state.selectedLicenseTitlesTemp = initialState.selectedLicenseTitlesTemp;
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setFilterOptions,
  setShouldTriggerFetch,
  setUserSearch,
  setSelectedWaterbodyIds,
  setSelectedWaterbodyIdsTemp,
  setSelectedWaterbodySubtypeIds,
  setSelectedWaterbodySubtypeIdsTemp,
  setStartFrom,
  setStartTo,
  setSelectedMembershipTypeIds,
  setSelectedMembershipTypeIdsTemp,
  setSelectedLicenseRangeTypes,
  setSelectedLicenseRangeTypesTemp,
  setSelectedLicenseTitles,
  setSelectedLicenseTitlesTemp,
  resetFilters,
} = sessionsFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { sessionsFilters: SessionsFiltersState }) => {
  const {
    userSearch,
    selectedWaterbodyIds,
    selectedWaterbodySubtypeIds,
    startFrom,
    startTo,
    selectedMembershipTypeIds,
    selectedLicenseRangeTypes,
    selectedLicenseTitles,
  } = state.sessionsFilters;

  return (
    !!userSearch ||
    !!selectedWaterbodyIds.length ||
    !!selectedWaterbodySubtypeIds.length ||
    !!startFrom ||
    !!startTo ||
    !!selectedMembershipTypeIds.length ||
    !!selectedLicenseRangeTypes.length ||
    !!selectedLicenseTitles.length
  );
};

export const activeFiltersCountSelector = (state: { sessionsFilters: SessionsFiltersState }) => {
  const {
    userSearch,
    selectedWaterbodyIds,
    selectedWaterbodySubtypeIds,
    startFrom,
    startTo,
    selectedMembershipTypeIds,
    selectedLicenseRangeTypes,
    selectedLicenseTitles,
  } = state.sessionsFilters;

  let count = 0;
  if (userSearch) count++;
  if (selectedWaterbodyIds.length > 0) count++;
  if (selectedWaterbodySubtypeIds.length > 0) count++;
  if (startFrom) count++;
  if (startTo) count++;
  if (selectedMembershipTypeIds.length > 0) count++;
  if (selectedLicenseRangeTypes.length > 0) count++;
  if (selectedLicenseTitles.length > 0) count++;

  return count;
};

export const fetchParamsSelector = (state: { sessionsFilters: SessionsFiltersState }) => {
  const {
    userSearch,
    selectedWaterbodyIds,
    selectedWaterbodySubtypeIds,
    startFrom,
    startTo,
    selectedMembershipTypeIds,
    selectedLicenseRangeTypes,
    selectedLicenseTitles,
  } = state.sessionsFilters;
  return {
    userSearch,
    selectedWaterbodyIds,
    selectedWaterbodySubtypeIds,
    startFrom,
    startTo,
    selectedMembershipTypeIds,
    selectedLicenseRangeTypes,
    selectedLicenseTitles,
  };
};

export const sessionsFiltersActions = sessionsFiltersSlice.actions;

export const sessionsFilters = sessionsFiltersSlice.reducer;
